/**
 * The only I/O for the insights feature: three RPCs, one mapping. Postgres
 * numeric comes back as strings through PostgREST, so every figure passes
 * through num(). Each .rpc() call uses a LITERAL function name and an inline
 * argument object — the rpcArgumentNames guard only sees literal call sites,
 * and a helper that took the NAME as a parameter would hide the call from it
 * (that exact hole shipped once already; see the guard's header comment).
 * `readRpc` below takes the whole call as a thunk, so the literal stays here.
 */
import { getSupabaseClient } from "@/config";
import { logError } from "@/services/logger";
import { ROW_CAP, num, mapEfficiencyRows } from "./adEfficiencyRow";
import type { AdEfficiencyRow, PlatformTotalRow } from "./insightTypes";
import type { TFn } from "@/utils/channelCorrelation";

/**
 * Both grains from one call, each with its OWN status.
 *
 * Per grain rather than one success flag because the two are consumed
 * independently: plan delivery is a fact about contracted rates and reads
 * only the ad-set rows, so Home's contract card must survive a failed AD read
 * (pinned by HomeOverview.test.tsx), while the ranking degrades to ad grain
 * on a failed AD-SET read. A failed grain is `{ success: false, error }` —
 * never a partial, never a zero.
 */
export interface EfficiencyResult {
  ads:
    | { success: true; rows: AdEfficiencyRow[]; latestDataDate: string | null; truncated: boolean }
    | { success: false; error: string };
  adSets:
    | { success: true; rows: AdEfficiencyRow[]; truncated: boolean }
    | { success: false; error: string };
}

type RpcOutcome = { raw: Record<string, unknown>[] } | { error: string };

/** One guarded RPC read. Guarded PER CALL so a thrown read of one grain cannot
 *  take the other down with it. */
const readRpc = async (
  call: () => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  label: string,
  fallback: string,
): Promise<RpcOutcome> => {
  try {
    const { data, error } = await call();
    if (error) return { error: error.message };
    return { raw: (data ?? []) as Record<string, unknown>[] };
  } catch (e) {
    logError("Insights", `${label} failed`, e);
    return { error: e instanceof Error ? e.message : fallback };
  }
};

/**
 * Ads AND ad sets, mapped together so the fixed-rate verdict can be inherited
 * from the ad set onto its ads (see mapEfficiencyRows). Neither read can be
 * paged — Range is ignored on an RPC POST — so `truncated` is the only honest
 * signal that a grain hit the ceiling.
 */
export const fetchEfficiency = async (
  workspaceId: string,
  t: TFn,
  days: number = 30,
): Promise<EfficiencyResult> => {
  const client = getSupabaseClient();
  const fallback = t("insights.loadFailedService");
  const [ads, sets] = await Promise.all([
    readRpc(() => client.rpc("get_ad_efficiency", { p_workspace: workspaceId, p_days: days }), "get_ad_efficiency", fallback),
    readRpc(() => client.rpc("get_ad_set_efficiency", { p_workspace: workspaceId, p_days: days }), "get_ad_set_efficiency", fallback),
  ]);
  const mapped = mapEfficiencyRows("raw" in ads ? ads.raw : [], "raw" in sets ? sets.raw : null);
  return {
    ads:
      "raw" in ads
        ? {
            success: true,
            rows: mapped.rows,
            latestDataDate: ads.raw.length ? ((ads.raw[0].latest_data_date as string | null) ?? null) : null,
            truncated: ads.raw.length >= ROW_CAP,
          }
        : { success: false, error: ads.error },
    adSets:
      "raw" in sets
        ? { success: true, rows: mapped.adSetRows ?? [], truncated: sets.raw.length >= ROW_CAP }
        : { success: false, error: sets.error },
  };
};

/**
 * Adset-level platform totals — the same grain the Dashboard aggregates.
 * Exists because ad-level rows miss PMax-shaped spend entirely (audit F1);
 * the cross-platform strip reads THIS, the ranking keeps reading ads. Since
 * migration 085 each row also carries the platform's own newest data date.
 */
export const fetchPlatformTotals = async (
  workspaceId: string,
  t: TFn,
  days: number = 30,
): Promise<{ success: boolean; data?: PlatformTotalRow[]; error?: string }> => {
  try {
    const { data, error } = await getSupabaseClient().rpc("get_platform_totals", {
      p_workspace: workspaceId,
      p_days: days,
    });
    if (error) return { success: false, error: error.message };
    const raw = (data ?? []) as Array<Record<string, unknown>>;
    return {
      success: true,
      data: raw.map((r) => ({
        platform: (r.platform as string) ?? "",
        spend: num(r.spend),
        conversions: num(r.conversions),
        // Null-preserving: a database without 085 has no date to offer, and
        // the tile must render nothing rather than "data through undefined".
        latestDataDate: (r.latest_data_date as string | null) ?? null,
      })),
    };
  } catch (e) {
    logError("Insights", "get_platform_totals failed", e);
    return { success: false, error: e instanceof Error ? e.message : t("insights.loadTotalsFailed") };
  }
};
